import { useQuery } from "@tanstack/react-query";
import { fetchSeriesQuizList } from "../services/quizSeriesService";

function normalizeQuizItem(item, index) {
  const isFree = item?.free_paid === "F";
  const isLocked = !isFree && Number(item?.payment_status || 0) !== 1;
  const attemptedQuestions = Number(item?.total_attempted_question || 0);
  const totalQuestions = Number(item?.total_questions || 0);
  const progress = totalQuestions > 0 ? Math.round((attemptedQuestions / totalQuestions) * 100) : 0;
  const liveType = item?.quiz_live_type === "L" ? "Live" : "Self-Paced";
  const pendingQuizInfo = item?.pending_quiz_info || null;
  const pendingAttempt =
    (Array.isArray(item?.attempted_quiz_list) ? item.attempted_quiz_list : []).find(
      (attempt) => String(attempt?.quiz_action || "").trim().toLowerCase() === "pending"
    ) || null;
  const resolvedPendingQuizInfo = pendingQuizInfo || pendingAttempt;

  return {
    id: item?.quiz_id || `${item?.quiz_name || "quiz"}-${index}`,
    quizId: Number(item?.quiz_id || 0),
    freePaid: String(item?.free_paid || '').toUpperCase(),
    paymentStatus: Number(item?.payment_status || 0),
    coinUnlockCost: Number(item?.coin_unlock_cost || 0),
    canUnlockWithAdCoins: [true, 1, '1'].includes(item?.can_unlock_with_ad_coins),
    subscriptionMessage: item?.subscription_message || '',
    isToday: [true, 1, '1'].includes(item?.is_today) || [true, 1, '1'].includes(item?.is_today_quiz),
    number: String(index + 1).padStart(2, "0"),
    title: item?.quiz_name || "Untitled Quiz",
    description: item?.quiz_description || "",
    plan: isFree ? "Free" : "Paid",
    planClass: isFree ? "free" : "prime",
    date: item?.start_date || "TBA",
    duration: `${Number(item?.duration || 0)} Minutes`,
    status: isLocked ? "Locked" : item?.solved_at || liveType,
    questions: String(totalQuestions),
    attempts: String(Number(item?.total_attempts || 0)),
    language: item?.available_languages || "English",
    marks: String(Number(item?.total_marks || 0)),
    progress,
    coins:
      isLocked && item?.can_unlock_with_ad_coins
        ? `${Number(item?.coin_unlock_cost || 0)} Coins`
        : "",
    statesId: Number(item?.states_id || item?.state_id || 0),
    categoryId: Number(item?.category_id || 0),
    pendingQuizInfo: resolvedPendingQuizInfo
      ? {
          assesment_batch_id: Number(resolvedPendingQuizInfo?.assesment_batch_id || 0),
          quiz_action: resolvedPendingQuizInfo?.quiz_action || "Pending",
          quiz_id: Number(resolvedPendingQuizInfo?.quiz_id || item?.quiz_id || 0),
          question_id: Number(resolvedPendingQuizInfo?.question_id || 0),
          attempted_count: Number(resolvedPendingQuizInfo?.attempted_count || 0),
          quiz_mode: String(resolvedPendingQuizInfo?.quiz_mode || "").trim().toUpperCase(),
        }
      : null,
  };
}

function normalizeSeriesInfo(info = {}) {
  return {
    seriesId: Number(info?.series_id || 0),
    name: info?.name || "",
    description: info?.description || "",
    totalQuiz: Number(info?.total_quiz || 0),
    startDate: info?.start_date || "",
    endDate: info?.end_date || "",
    quizType: info?.quiz_type || "",
    enrolledStatus: Number(info?.enrolled_status || 0),
    totalEnrollments: Number(info?.total_enrollments || 0),
  };
}

export function useSeriesQuizList(params = {}, options = {}) {
  return useQuery({
    queryKey: ["seriesQuizList", params],
    queryFn: async () => {
      const { payload } = await fetchSeriesQuizList(params);
      const rawQuizList = Array.isArray(payload?.quiz_list) ? payload.quiz_list : [];
      const pagination = payload?.pagination || {};
      const seriesInfo = normalizeSeriesInfo(payload?.series_info || {});

      return {
        bannerImage: payload?.banner_image || "",
        advertEarnedCoins: Number(payload?.advert_earned_coins || 0),
        stateName: payload?.state_name || "",
        categoryName: payload?.category_name || "",
        seriesName: seriesInfo.name || payload?.series_name || payload?.name || "",
        seriesInfo,
        quizList: rawQuizList.map((item, index) => normalizeQuizItem(item, index)),
        totalRecords: Number(payload?.pagination?.totalRecords || rawQuizList.length || 0),
        currentPage: Number(pagination?.currentPage || params?.page || 1),
        totalPages: Number(pagination?.totalPages || 1),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        bannerImage: "",
        stateName: "",
        categoryName: "",
        seriesName: "",
        seriesInfo: normalizeSeriesInfo(),
        quizList: [],
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
      },
  });
}
